<script context="module" lang="ts">
	export type NewsletterFormIntents = 'default' | 'small';
	export type NewsletterFormIntentsMap<T> = { [key in NewsletterFormIntents]: T };
</script>

<script lang="ts">
	import InputWrapper from '$lib/components/Simulations/Components/Survey/fields/InputWrapper.svelte';
	import __ from '$lib/stores/TranslationStore';
	import outin from '$lib/utils/transitions/outin';
	import { createEventDispatcher } from 'svelte';
	import Loading from '../Loading.svelte';
	import { dev } from '$app/environment';
	import Cta from '../Cta.svelte';

	export let intent: NewsletterFormIntents = 'default';

	const isIntentDefault = intent === 'default';
	const dispatch = createEventDispatcher();

	let contactName: string | undefined;
	let email: string | undefined;
	let gdpr: boolean | undefined;
	let emkt: boolean | undefined;

	let errors: { [key: string]: string | null } = {
		name: null,
		email: null,
		gdpr: null,
		emkt: null
	};

	let timeoutId: number | null | undefined;

	let loading: boolean = false;

	function clearError(key: string) {
		if (!(key in errors)) return;
		if (!errors[key]) return;

		errors[key] = null;
	}

	async function submitNewsletter(e: Event) {
		const method = 'POST';
		const body = new FormData(e.target as HTMLFormElement);

		loading = true;

		try {
			const response = await fetch('/api/newsletter/new-subscriber', { method, body });
			const res = await response.json();

			loading = false;

			if (res.status === 422) {
				let responseErrors = res?.data?.data?.errors;
				console.log('response errors', responseErrors);
				Object.keys(responseErrors).forEach((key) => {
					let keyErrors: string[] | null = responseErrors[key] || null;
					if (!keyErrors) return;

					if (Array.isArray(keyErrors)) {
						if (keyErrors.length === 0) return;
						responseErrors[key] = keyErrors.map((err) => $__(err));

						return;
					}

					responseErrors[key] = $__(keyErrors);
				});

				errors = { ...errors, ...responseErrors };
				return;
			}

			dispatch('switch-info', { ok: res.status === 200 });
		} catch (error) {
			console.log('DEBUG', error);
			loading = false;
			dispatch('switch-info', { ok: false });
		}
	}

	$: hasErrors = Object.values(errors).some((fieldError: string | null) => fieldError !== null);
	$: canSubmit = !timeoutId && !hasErrors && contactName && email && gdpr && emkt;
	$: console.log(errors);

	const gdprTextMap: NewsletterFormIntentsMap<string> = {
		default: $__(
			'Declaro que aceito a <a class="text-primary font-semibold" href="/politica-de-protecao-de-dados">Política de proteção de dados</a> e os <a class="text-primary font-semibold" href="/termos-e-condicoes">Termos e Condições</a> e que os meus dados pessoais sejam processados para aconselhamento em formas de poupança junto dos parceiros da Financefy SA.'
		),
		small: $__(
			'Declaro que aceito a <a class="text-primary font-semibold" href="/politica-de-protecao-de-dados">Política de proteção de dados</a> e os <a class="text-primary font-semibold" href="/termos-e-condicoes">Termos e Condições</a>.'
		)
	};

	const emktTextMap: NewsletterFormIntentsMap<string> = {
		default: $__(
			'Autorizo a Financefy SA a utilizar os meus dados pessoais para que possa receber conteúdos informativos relevantes, sabendo que posso alterar esta opção a qualquer momento.'
		),
		small: $__(
			'Autorizo a Financefy SA a utilizar os meus dados pessoais para que possa receber conteúdos informativos relevantes.'
		)
	};
</script>

<div
	id="newsletter"
	class="grid:cols-1 grid items-center justify-between gap-x-28 gap-y-10 {isIntentDefault
		? 'gap-y-10 md:grid-cols-2'
		: 'gap-y-7'}"
>
	<div class="flex flex-col gap-y-5 self-center md:flex-1">
		<h3
			class="font-semibold uppercase text-primary-light {isIntentDefault ? 'text-sm' : 'text-xs'}"
		>
			Newsletter
		</h3>
		{#if isIntentDefault}
			<h2 class="text-5xl">
				{$__('Quer poupar com créditos e seguros?')}
			</h2>
			<p class="text-xl text-bland">
				{$__('Assine a nossa newsletter e não perca nenhum conteúdo.')}
			</p>
		{:else if intent === 'small'}
			<h2 class="text-2xl">
				{@html $__('Assine a nossa newsletter e não <strong>perca nenhum conteúdo.</strong>')}
			</h2>
		{/if}
	</div>
	<form
		class="flex flex-1 flex-col {isIntentDefault ? 'gap-y-8' : 'gap-y-7'}"
		on:submit|preventDefault={submitNewsletter}
	>
		<InputWrapper
			light
			name="name"
			placeholder={$__('Nome')}
			label={$__('Nome')}
			customErrorMessage={errors['name']}
			bind:value={contactName}
			on:input={() => clearError('name')}
		/>
		<InputWrapper
			light
			name="email"
			placeholder="E-mail"
			label="E-mail"
			inputmode="email"
			customErrorMessage={errors['email']}
			bind:value={email}
			on:input={() => clearError('email')}
		/>
		<div class="items-top flex">
			<div class="-mt-1 basis-1">
				<input
					id="gdpr"
					type="checkbox"
					name="gdpr"
					bind:checked={gdpr}
					class="w-[13px] cursor-pointer bg-gray-100 text-secondary"
				/>
			</div>
			<label for="gdpr" class="ml-4 text-xs font-medium text-bland">
				{@html gdprTextMap[intent]}
			</label>
		</div>
		<div class="items-top -mt-1 flex">
			<div class="basis-1">
				<input
					id="emkt"
					type="checkbox"
					name="emkt"
					value="1"
					bind:checked={emkt}
					class="w-[13px] cursor-pointer bg-gray-100 text-secondary"
				/>
			</div>
			<label for="emkt" class="ml-4 text-xs font-medium text-bland">
				{@html emktTextMap[intent]}
			</label>
		</div>
		<!-- <span class="text-red-500">{validationErrors?.emkt ?? ''}</span> -->
		<Cta
			type="button"
			class="px-7 text-center text-xl font-semibold {isIntentDefault ? 'py-4' : 'py-2'} {canSubmit
				? 'bg-primary-light text-slate'
				: 'bg-contact-btn text-light'}"
			props={{
				type: 'submit',
				disabled: !canSubmit
			}}
		>
			{#if !loading}
				<span>{$__('Assinar')}</span>
			{:else}
				<Loading />
			{/if}
		</Cta>
	</form>
</div>
